'use client';

import { useState, useRef, useEffect } from 'react';
import { ResponsiveHeader } from './ResponsiveHeader';
import { ResponsiveFooter } from './ResponsiveFooter';
import PricingTiers from './PricingTiers';
import {
  Mail,
  Phone,
  MapPin,
  Send,
  Facebook,
  Instagram,
  Linkedin,
  Youtube,
  Copy,
} from 'lucide-react';
import { toast } from 'sonner';
import { motion, useInView } from 'motion/react';
import ReCAPTCHA from 'react-google-recaptcha';

export function ResponsiveContactUs() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    solution: '',
    message: '',
    website: '', // honeypot
  });

  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [showCaptcha, setShowCaptcha] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      const msg = url.searchParams.get('message');
      const sol = url.searchParams.get('solution');

      if (msg || sol) {
        setFormData((prev) => ({
          ...prev,
          message: msg || prev.message,
          solution: sol || prev.solution,
        }));

        setTimeout(() => {
          const contactSection = document.getElementById('contact');
          if (contactSection) {
            contactSection.scrollIntoView({ behavior: 'smooth' });
          }
        }, 500);
      }
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    const { name, email, phone, solution, message } = formData;

    if (!name.trim() || !email.trim() || !phone.trim() || !solution.trim() || !message.trim()) {
      toast.error('Missing required fields', {
        description: 'Please fill out all required sections of the form.',
      });
      return;
    }

    if (!captchaToken) {
      toast.error('Please complete the CAPTCHA', {
        description: 'We need to verify you are a human.',
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Invalid email address', {
        description: 'Please enter a valid email format.',
      });
      return;
    }

    const scriptUrl = process.env.NEXT_PUBLIC_GOOGLE_SCRIPT_URL;

    if (!scriptUrl) {
      toast.error('Email service is not configured.', {
        description: 'Please check the .env file for the Google Script URL.',
      });
      return;
    }

    const toastId = toast.loading('Sending your message...');

    try {
      const response = await fetch(scriptUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'text/plain;charset=utf-8',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          solution: formData.solution,
          message: formData.message,
          website: formData.website,
          'g-recaptcha-response': captchaToken,
        }),
      });

      const text = await response.text();
      console.log('Response status:', response.status);
      console.log('Response body:', text);

      let result;
      try {
        result = JSON.parse(text);
      } catch {
        throw new Error(`Server response was not valid JSON: ${text.substring(0, 100)}...`);
      }

      if (result.result === 'success') {
        toast.dismiss(toastId);
        toast.success('Message sent successfully!', {
          description: "We'll get back to you as soon as possible.",
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          solution: '',
          message: '',
          website: '',
        });
        setCaptchaToken(null);
      } else {
        throw new Error(result.error || 'Unknown error');
      }
    } catch (error) {
      toast.dismiss(toastId);
      console.error('FAILED...', error);
      toast.error('Failed to send message.', {
        description: 'Please try again later or contact us directly.',
      });
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleCopy = (text: string, type: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${type} copied to clipboard!`, {
      icon: <Copy className="w-4 h-4" />,
    });
  };

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <div className="min-h-screen bg-zinc-900 flex flex-col">
      <ResponsiveHeader />

      {/* Hero Section */}
      <section className="px-4 sm:px-6 lg:px-8 sm:pt-40 lg:pt-48 lg:pb-20 pt-[220px] pr-[32px] pb-[10px] pl-[32px]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-1 pt-8 sm:pt-12 lg:pt-16">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-6">
              Contact Zumify for a{' '}
              <span className="bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent">
                Project
              </span>
            </h1>
            <p className="text-lg sm:text-xl lg:text-2xl text-stone-300 max-w-3xl mx-auto">
              Have an idea in mind or a problem to solve? We’d love to hear it. <br/>
              <br/>
              Let’s discuss how we can design conversion-focused websites, scalable apps, and intelligent AI agents & systems built to help your business grow.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section id="contact" ref={ref} className="px-4 sm:px-6 lg:px-8 py-12 overflow-hidden">
        <div className="max-w-7xl mx-auto w-full">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Contact Form - Slides in from LEFT */}
            <motion.div
              className="bg-zinc-800 border-2 border-purple-600/20 rounded-3xl p-6 sm:p-8 lg:p-10 w-full min-w-0"
              style={{ willChange: 'transform' }}
              initial="hidden"
              animate={isInView ? 'visible' : 'hidden'}
              variants={{
                hidden: { opacity: 0, x: -60 },
                visible: {
                  opacity: 1,
                  x: 0,
                  transition: { duration: 0.7, ease: [0.21, 0.47, 0.32, 0.98] },
                },
              }}
            >
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-6 break-words">
                Get a quote for your software needs in Hawaiʻi
              </h2>
              <form
                onSubmit={handleSubmit}
                className="space-y-6 w-full"
                onFocusCapture={() => setShowCaptcha(true)}
                onClickCapture={() => setShowCaptcha(true)}
                onChangeCapture={() => setShowCaptcha(true)}
              >
                <input
                  type="text"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  className="hidden"
                  autoComplete="off"
                />

                <div className="w-full">
                  <label htmlFor="name" className="block text-white mb-2 text-base sm:text-lg">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-stone-800 border-b-2 border-white/20 text-white rounded-lg focus:outline-none focus:border-purple-600 transition-colors text-base"
                    placeholder="First and Last Name"
                  />
                </div>

                <div className="w-full">
                  <label htmlFor="email" className="block text-white mb-2 text-base sm:text-lg">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-stone-800 border-b-2 border-white/20 text-white rounded-lg focus:outline-none focus:border-purple-600 transition-colors text-base"
                    placeholder="your@email.com"
                  />
                </div>

                <div className="w-full">
                  <label htmlFor="phone" className="block text-white mb-2 text-base sm:text-lg">
                    Phone
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-stone-800 border-b-2 border-white/20 text-white rounded-lg focus:outline-none focus:border-purple-600 transition-colors text-base"
                    placeholder="(000) 000-0000"
                  />
                </div>

                <div className="w-full">
                  <label htmlFor="company" className="block text-white mb-2 text-base sm:text-lg">
                    Company / Brand Name <span className="text-stone-400 text-sm">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-stone-800 border-b-2 border-white/20 text-white rounded-lg focus:outline-none focus:border-purple-600 transition-colors text-base"
                    placeholder="Name of Company/Brand"
                  />
                </div>

                <div className="w-full">
                  <label htmlFor="solution" className="block text-white mb-2 text-base sm:text-lg">
                    What Solution are you interested in?
                  </label>
                  <div className="relative">
                    <select
                      id="solution"
                      name="solution"
                      value={formData.solution}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-stone-800 border-b-2 border-white/20 text-white rounded-lg focus:outline-none focus:border-purple-600 transition-colors text-base appearance-none cursor-pointer"
                    >
                      <option value="" disabled>
                        Select an option
                      </option>
                      <option value="Website">Website</option>
                      <option value="Mobile App">Mobile App</option>
                      <option value="Prototype/MVP">Prototype/MVP</option>
                      <option value="Design Job">Design Job</option>
                      <option value="AI Agent">AI Agent</option>
                      <option value="Software System">Software System Ex: Dashboard</option>
                      <option value="Other">Other</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-white">
                      <svg
                        className="fill-current h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="w-full">
                  <label htmlFor="message" className="block text-white mb-2 text-base sm:text-lg">
                    Tell us more about your project?
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 bg-stone-800 border-b-2 border-white/20 text-white rounded-lg focus:outline-none focus:border-purple-600 transition-colors resize-none text-base"
                    placeholder="Tell us about your project..."
                  />
                </div>

                {process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY && showCaptcha ? (
                  <div className="w-full flex justify-center sm:justify-start">
                    <ReCAPTCHA
                      sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
                      onChange={(token) => setCaptchaToken(token)}
                      theme="dark"
                    />
                  </div>
                ) : null}

                <button
                  type="submit"
                  className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full hover:scale-105 transition-transform duration-300 flex items-center justify-center gap-2 text-base sm:text-lg"
                >
                  <Send className="w-5 h-5 flex-shrink-0" />
                  <span className="whitespace-nowrap">Send Message</span>
                </button>
              </form>
            </motion.div>

            {/* Contact Info & Image - Slides in from RIGHT */}
            <motion.div
              className="space-y-8 w-full min-w-0"
              style={{ willChange: 'transform' }}
              initial="hidden"
              animate={isInView ? 'visible' : 'hidden'}
              variants={{
                hidden: { opacity: 0, x: 60 },
                visible: {
                  opacity: 1,
                  x: 0,
                  transition: { duration: 0.7, ease: [0.21, 0.47, 0.32, 0.98] },
                },
              }}
            >
              {/* Image */}
              <div className="relative w-full aspect-video rounded-3xl overflow-hidden shadow-2xl shadow-purple-500/20">
                <img
                  src="/images/858cd59eb0c904b7ce4495ad49f437f0fe7be5cd.png"
                  alt="Contact Zumify Futuristic Image"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>

              {/* Contact Details */}
              <div className="bg-zinc-800 border-2 border-purple-600/20 rounded-3xl p-6 sm:p-8 space-y-6 w-full">
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-6 break-words">
                  Contact Information
                </h3>

                <div
                  className="flex items-start gap-4 cursor-pointer group"
                  onClick={() => handleCopy('contact@zumify.co', 'Email')}
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-white font-semibold mb-1 text-base sm:text-lg">Email</h4>
                    <p className="text-stone-300 group-hover:text-purple-400 transition-colors text-sm sm:text-base break-words flex items-center gap-2">
                      contact@zumify.co
                      <Copy className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </p>
                  </div>
                </div>

                <div
                  className="flex items-start gap-4 cursor-pointer group"
                  onClick={() => handleCopy('808-341-4194', 'Phone number')}
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-white font-semibold mb-1 text-base sm:text-lg">Phone</h4>
                    <p className="text-stone-300 group-hover:text-purple-400 transition-colors text-sm sm:text-base whitespace-nowrap flex items-center gap-2">
                      808-341-4194
                      <Copy className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </p>
                  </div>
                </div>

                <div
                  className="flex items-start gap-4 cursor-pointer group"
                  onClick={() => handleCopy('Honolulu, HI', 'Location')}
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-white font-semibold mb-1 text-base sm:text-lg">Location</h4>
                    <p className="text-stone-300 group-hover:text-purple-400 transition-colors text-sm sm:text-base flex items-center gap-2">
                      Honolulu, HI
                      <Copy className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </p>
                  </div>
                </div>

                {/* Social Media */}
                <div className="pt-6 border-t border-white/10">
                  <h4 className="text-white font-semibold mb-4 text-base sm:text-lg">Follow Us</h4>
                  <div className="flex gap-4 flex-wrap">
                    {[
                      {
                        icon: <Instagram className="w-5 h-5 text-white" />,
                        label: 'Instagram',
                        url: 'https://www.instagram.com/zumify_hawaii/',
                      },
                      {
                        icon: <Facebook className="w-5 h-5 text-white" />,
                        label: 'Facebook',
                        url: 'https://www.facebook.com/profile.php?id=61585515988041',
                      },
                      {
                        icon: (
                          <svg
                            className="w-5 h-5 text-white"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                          </svg>
                        ),
                        label: 'X',
                        url: 'https://x.com/zumify_hawaii',
                      },
                      {
                        icon: <Linkedin className="w-5 h-5 text-white" />,
                        label: 'LinkedIn',
                        url: 'https://www.linkedin.com/company/zumify/',
                      },
                      {
                        icon: (
                          <svg
                            className="w-5 h-5 text-white"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                          </svg>
                        ),
                        label: 'TikTok',
                        url: 'https://www.tiktok.com/@zumify_hawaii',
                      },
                      {
                        icon: <Youtube className="w-5 h-5 text-white" />,
                        label: 'YouTube',
                        url: 'https://www.youtube.com/@zumify_hawaii',
                      },
                    ].map((social, index) => (
                      <a
                        key={index}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center hover:bg-purple-700 transition-colors flex-shrink-0"
                        aria-label={social.label}
                      >
                        {social.icon}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      {/* Pricing Tiers Section */}
      <PricingTiers />

      <ResponsiveFooter />
    </div>
  );
}
